import React, { useMemo, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ShoppingCart, Mail, Check, Beaker,
  Tag, Package, Box, MapPin, Phone, ChevronRight, Sparkles, FileDown,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { DownloadCatalogButton } from './DownloadCatalogButton';
import { productsData, subCategoryNames, categoryNames } from '../data/productsData';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUBCAT_ICONS = {
  kitchen: '🍽️',
  laundry: '👕',
  home: '🏠',
  'personal-care': '💧',
};
const SUBCAT_GRADIENTS = {
  kitchen: 'from-orange-500 to-amber-600',
  laundry: 'from-sky-500 to-blue-600',
  home: 'from-emerald-500 to-teal-600',
  'personal-care': 'from-pink-500 to-rose-600',
};

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, direction } = useLanguage();

  const product = useMemo(() => productsData.find((p) => p.id === id), [id]);

  // Compute related products BEFORE any early return (hooks rule)
  const related = useMemo(
    () => product
      ? productsData.filter((p) => p.subCategory === product.subCategory && p.id !== product.id).slice(0, 4)
      : [],
    [product]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!product) {
    return (
      <section className="min-h-[60vh] flex items-center justify-center" dir={direction}>
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">
            {language === 'ar' ? 'المنتج غير موجود' : language === 'en' ? 'Product not found' : 'Produit introuvable'}
          </h2>
          <Link to="/catalogue" className="text-blue-600 hover:underline">
            ← {language === 'ar' ? 'العودة إلى الكتالوج' : language === 'en' ? 'Back to catalog' : 'Retour au catalogue'}
          </Link>
        </div>
      </section>
    );
  }

  const name = language === 'ar' ? product.nameAr : language === 'en' ? product.nameEn : product.nameFr;
  const desc = language === 'ar' ? product.descriptionAr : language === 'en' ? product.descriptionEn : product.descriptionFr;
  const features = language === 'ar' ? product.featuresAr : language === 'en' ? product.featuresEn : product.featuresFr;
  const subCatLabel = subCategoryNames[product.subCategory][language];
  const catLabel = categoryNames[product.category][language];
  const gradient = SUBCAT_GRADIENTS[product.subCategory];
  const icon = SUBCAT_ICONS[product.subCategory];

  const usages = product.category === 'industrial' ? [
    language === 'ar' ? 'الفنادق والمنتجعات' : language === 'en' ? 'Hotels & Resorts' : 'Hôtels & Resorts',
    language === 'ar' ? 'المطاعم والمقاهي' : language === 'en' ? 'Restaurants & Cafes' : 'Restaurants & Cafés',
    language === 'ar' ? 'المغاسل والكوي' : language === 'en' ? 'Laundries & Dry-cleaners' : 'Pressings & Blanchisseries',
    language === 'ar' ? 'المنشآت الصحية' : language === 'en' ? 'Healthcare facilities' : 'Établissements de soins',
  ] : [
    language === 'ar' ? 'المنزل العائلي' : language === 'en' ? 'Family home' : 'Maison familiale',
    language === 'ar' ? 'الاستخدام اليومي' : language === 'en' ? 'Daily use' : 'Usage quotidien',
    language === 'ar' ? 'البوتيكات والمكاتب' : language === 'en' ? 'Boutiques & offices' : 'Boutiques & bureaux',
    language === 'ar' ? 'المتاجر الكبرى' : language === 'en' ? 'Supermarkets' : 'Grandes surfaces',
  ];

  const sampleSubject = encodeURIComponent(
    language === 'ar'
      ? `طلب عينة — ${name} (${product.size})`
      : language === 'en'
      ? `Sample request — ${name} (${product.size})`
      : `Demande d'échantillon — ${name} (${product.size})`
  );

  const specs = [
    { label: language === 'ar' ? 'الفئة' : language === 'en' ? 'Category' : 'Catégorie', value: catLabel, icon: Tag },
    { label: language === 'ar' ? 'النوع' : language === 'en' ? 'Type' : 'Type', value: subCatLabel, icon: Beaker },
    { label: language === 'ar' ? 'الحجم' : language === 'en' ? 'Size' : 'Conditionnement', value: product.size, icon: Package },
    { label: language === 'ar' ? 'القناة' : language === 'en' ? 'Channel' : 'Canal', value: product.category === 'industrial'
        ? (language === 'ar' ? 'احترافي / صناعي' : language === 'en' ? 'Professional / Industrial' : 'Professionnel / Industriel')
        : (language === 'ar' ? 'بيع بالتجزئة' : language === 'en' ? 'Retail' : 'Retail'), icon: ShoppingCart },
    { label: language === 'ar' ? 'المرجع' : language === 'en' ? 'Reference' : 'Référence', value: product.id.toUpperCase(), icon: Box },
    { label: language === 'ar' ? 'بلد الصنع' : language === 'en' ? 'Origin' : 'Origine', value: language === 'ar' ? 'صنع في تونس' : 'Made in Tunisia 🇹🇳', icon: MapPin },
  ];

  return (
    <section className="bg-gradient-to-b from-blue-50/40 via-white to-white min-h-screen" dir={direction}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-testid="breadcrumbs">
          <Link to="/" className="hover:text-blue-600">
            {language === 'ar' ? 'الرئيسية' : language === 'en' ? 'Home' : 'Accueil'}
          </Link>
          <ChevronRight className={`w-4 h-4 ${direction === 'rtl' ? 'rotate-180' : ''}`} />
          <Link to="/catalogue" className="hover:text-blue-600">
            {language === 'ar' ? 'الكتالوج' : language === 'en' ? 'Catalog' : 'Catalogue'}
          </Link>
          <ChevronRight className={`w-4 h-4 ${direction === 'rtl' ? 'rotate-180' : ''}`} />
          <span className="text-gray-400">{subCatLabel}</span>
          <ChevronRight className={`w-4 h-4 ${direction === 'rtl' ? 'rotate-180' : ''}`} />
          <span className="text-gray-700 font-semibold truncate max-w-[200px]">{name}</span>
        </nav>

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-6"
        >
          <ArrowLeft className={`w-4 h-4 ${direction === 'rtl' ? 'rotate-180' : ''}`} />
          {language === 'ar' ? 'العودة' : language === 'en' ? 'Back' : 'Retour'}
        </button>

        {/* Main grid: image + info */}
        <div className="grid lg:grid-cols-2 gap-10 mb-16">

          {/* Left: Image */}
          <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100 flex items-center justify-center relative overflow-hidden min-h-[500px]" data-testid="product-image">
            {/* Background decorative gradient */}
            <div className={`absolute -top-20 -right-20 w-80 h-80 bg-gradient-to-br ${gradient} opacity-10 rounded-full blur-3xl`} />
            <div className={`absolute -bottom-20 -left-20 w-80 h-80 bg-gradient-to-br ${gradient} opacity-10 rounded-full blur-3xl`} />

            {/* Badges on image */}
            <div className="absolute top-6 left-6 flex flex-col gap-2 z-10">
              <span className={`bg-gradient-to-r ${gradient} text-white px-4 py-2 rounded-full text-sm font-bold shadow-md flex items-center gap-2`}>
                <span className="text-base">{icon}</span> {subCatLabel}
              </span>
              <span className="bg-gray-900/85 backdrop-blur-sm text-white px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider w-fit">
                {product.category === 'industrial'
                  ? (language === 'ar' ? 'احترافي / صناعي' : language === 'en' ? 'Pro / Industrial' : 'Pro / Industriel')
                  : 'Retail'}
              </span>
            </div>
            <div className="absolute top-6 right-6 z-10">
              <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-lg font-bold shadow-md">
                {product.size}
              </span>
            </div>

            {/* Image */}
            {product.hasImage ? (
              <img
                src={product.image}
                alt={name}
                style={{ mixBlendMode: 'multiply' }}
                className="max-h-[420px] max-w-full object-contain relative z-0"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-300 gap-4">
                <Package className="w-32 h-32" strokeWidth={1} />
                <span className="text-sm uppercase tracking-wider font-semibold">
                  {language === 'ar' ? 'صورة قريباً' : language === 'en' ? 'Photo coming soon' : 'Photo bientôt'}
                </span>
              </div>
            )}
          </div>

          {/* Right: Info */}
          <div className="flex flex-col">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold mb-4 w-fit">
              <Sparkles className="w-3 h-3" />
              {language === 'ar' ? 'منتج أوليون' : language === 'en' ? 'Olion Product' : 'Produit Olion'}
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight" data-testid="product-name">
              {name}
            </h1>

            <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-8" data-testid="product-description">
              {desc}
            </p>

            {/* Key features */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-6 mb-8 border border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Sparkles className={`w-4 h-4 text-orange-500`} />
                {language === 'ar' ? 'الخصائص الرئيسية' : language === 'en' ? 'Key Features' : 'Caractéristiques clés'}
              </h3>
              <ul className="space-y-3" data-testid="product-features">
                {features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-gray-700">
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    </div>
                    <span className="leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link
                to={`/?subject=${sampleSubject}#contact`}
                data-testid="cta-sample"
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-4 rounded-full text-base font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                {language === 'ar' ? 'طلب عينة' : language === 'en' ? 'Request Sample' : "Demander un échantillon"}
              </Link>
              <a
                href="https://olion.odoo.com/shop"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-shop"
                className="flex-1 bg-blue-700 hover:bg-blue-800 text-white px-6 py-4 rounded-full text-base font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                {language === 'ar' ? 'اشتري عبر الإنترنت' : language === 'en' ? 'Buy Online' : 'Acheter en ligne'}
              </a>
            </div>

            <DownloadCatalogButton
              language={language}
              variant="link"
              short
              testid="cta-pdf"
              className="text-sm text-blue-700 hover:text-blue-900 font-semibold px-0 py-0 shadow-none rounded-none inline-flex bg-transparent hover:bg-transparent"
            />
          </div>
        </div>

        {/* Specifications Table */}
        <div className="mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <div className="w-1 h-8 bg-orange-500 rounded-full" />
            {language === 'ar' ? 'المواصفات التقنية' : language === 'en' ? 'Technical Specifications' : 'Spécifications techniques'}
          </h2>
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100" data-testid="spec-table">
              {specs.map((s, idx) => {
                const Icon = s.icon;
                return (
                  <div key={idx} className="p-6 hover:bg-blue-50/30 transition-colors">
                    <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider font-semibold mb-2">
                      <Icon className="w-3.5 h-3.5" />
                      {s.label}
                    </div>
                    <div className="text-base font-bold text-gray-900 break-words">{s.value}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recommended uses */}
        <div className="mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <div className="w-1 h-8 bg-orange-500 rounded-full" />
            {language === 'ar' ? 'الاستخدامات الموصى بها' : language === 'en' ? 'Recommended Uses' : "Utilisations recommandées"}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {usages.map((u, idx) => (
              <div key={idx} className={`bg-gradient-to-br ${gradient} rounded-2xl p-5 text-white shadow-lg`}>
                <div className="text-2xl mb-2">{icon}</div>
                <div className="font-semibold text-sm">{u}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <div className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-1 h-8 bg-orange-500 rounded-full" />
              {language === 'ar' ? 'منتجات ذات صلة' : language === 'en' ? 'Related Products' : 'Produits liés'}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="related-products">
              {related.map((rp) => {
                const rpName = language === 'ar' ? rp.nameAr : language === 'en' ? rp.nameEn : rp.nameFr;
                return (
                  <Link
                    key={rp.id}
                    to={`/catalogue/${rp.id}`}
                    data-testid={`related-${rp.id}`}
                    className="group bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 overflow-hidden transition-all duration-300 hover:-translate-y-1 flex flex-col"
                  >
                    <div className="relative h-48 bg-white flex items-center justify-center p-4 overflow-hidden">
                      {rp.hasImage ? (
                        <img
                          src={rp.image}
                          alt={rpName}
                          style={{ mixBlendMode: 'multiply' }}
                          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <Package className="w-16 h-16 text-gray-200" strokeWidth={1} />
                      )}
                      <span className={`absolute top-3 right-3 bg-gradient-to-r ${gradient} text-white px-2.5 py-1 rounded-full text-xs font-bold`}>
                        {rp.size}
                      </span>
                    </div>
                    <div className="p-4 flex-1 flex flex-col bg-gray-50 border-t border-gray-100">
                      <h3 className="text-sm font-bold text-gray-900 group-hover:text-blue-700 transition-colors flex-1">
                        {rpName}
                      </h3>
                      <div className="text-xs text-blue-600 font-semibold mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                        {language === 'ar' ? 'عرض' : language === 'en' ? 'View' : 'Voir'}
                        <ArrowRight className={`w-3 h-3 ${direction === 'rtl' ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Bottom CTA bar */}
        <div className="bg-gradient-to-r from-blue-700 via-blue-800 to-blue-900 rounded-3xl p-10 text-white shadow-2xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-3">
                {language === 'ar'
                  ? 'بحاجة إلى عرض أسعار بالجملة ؟'
                  : language === 'en'
                  ? 'Need a wholesale quote?'
                  : 'Besoin d\'un devis en gros ?'}
              </h3>
              <p className="text-blue-100 leading-relaxed">
                {language === 'ar'
                  ? 'فريقنا التجاري يجيب على طلبك خلال 24 ساعة بشروط مخصصة للموزعين والشركاء الدوليين.'
                  : language === 'en'
                  ? 'Our commercial team responds to your request within 24 hours, with custom terms for distributors and international partners.'
                  : "Notre équipe commerciale répond à votre demande sous 24h, avec des conditions sur-mesure pour distributeurs et partenaires internationaux."}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <a
                href="tel:+21699804002"
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-4 rounded-full text-base font-bold shadow-xl hover:scale-105 transition-transform inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                +216 99 804 002
              </a>
              <Link
                to="/#contact"
                className="bg-white text-blue-800 hover:bg-blue-50 px-6 py-4 rounded-full text-base font-bold shadow-xl hover:scale-105 transition-transform inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                {language === 'ar' ? 'اتصل بنا' : language === 'en' ? 'Contact' : 'Nous contacter'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
